<template>
  <div class="container-fluid mb-7 mb-md-5">
    <PageHeaderWithTab
      pre-title="락커"
      title="락커 리스트"
      :tab-menu="menus"
      :has-amount="false"
      @change="onChangeTab"
    />

    <div class="locker-container">
      <div
        v-if="!isPageLoading"
        class="locker-item-container"
        ref="locker-item-container"
      >
        <LockerItem
          v-for="locker in lockers"
          :key="locker.id"
          :data="locker"
          :size="itemSize"
          @click="handleClickLocker"
        />

        <div
          v-show="lockers.length > 0"
          class="locker-item-area"
        >
          <div
            class="locker-item plus"
            :style="{'width' : `${itemSize}px`, 'height': `${itemSize}px`}"
            @click="handleClickMakeRooms"
          >
            <span class="icon ic-plus-inactive" />
            <div class="plus-words">
              락커 추가
            </div>
          </div>
        </div>

        <div
          v-show="lockers.length === 0"
          class="empty-message"
        >
          <div style="text-align: center;">
            <warning-message :message="`등록된 ${curMenu} 락커가 없습니다.`" />
            <el-button
              class="black"
              @click="handleClickMakeRooms"
            >
              락커 등록
            </el-button>
          </div>
        </div>
      </div>
      <div
        v-else
        class="locker-item-container loading"
      >
        <el-spinner :stroke-width="2" />
      </div>
    </div>
    <booking-exist-warning-dialog 
      :title="`${targetLocker ? targetLocker.name : ''} 전체 내역`"
      :message="`현재 락커를 이용 중이거나 이용 예정/만료된 회원 리스트입니다.
	  			<p class='confirm-description__sub'>(각 회원 상세 페이지에 등록된 락커 내역 기준)</p>`"
      :dialog-visible="lockerHistoryDialogVisible"
      confirm-button-text="확인"
      :cancel-button-text="null"
      :has-slot="true"
      :show-notice="false"
      @confirm="closeDialog()"
    >
      <ul v-if="targetLocker">
        <li 
          :class="`locker-history-dialog__item-wrapper${isOccupied(history) ? ' occupied' : ''}`" 
          v-for="history in targetLocker.histories.slice().reverse()"
          :key="history.id"
        >
          <span class="locker-history-dialog__item__name">
            {{ history.name }}
          </span>
          <span
            class="locker-history-dialog__item__duration"
          >
            {{ duration(history) }}
          </span>
        </li>
      </ul>
    </booking-exist-warning-dialog>
  </div>
</template>

<script>
import WarningMessage from '@/components/element-ui/WarningMessage';
import LockerItem from './LockerItem';
import PageHeaderWithTab from '@/components/element-ui/PageHeaderWithTab';
import BookingExistWarningDialog from '@/components/BookingExistWarningDialog';
import FiflFooter from '@/components/FiflFooter';
import { fiflboxApi } from '@/utils/api';
import { computedGetters } from '@/utils/vuex-helper';
import dateFns from 'date-fns';
import { LOCKER_STATUS } from '@/constants';
import resizePageMixin from '@/mixins/resizePageMixin';

export default {
  name: 'Locker',
  components: {
    FiflFooter,
    PageHeaderWithTab,
    LockerItem,
    WarningMessage,
    BookingExistWarningDialog
  },
  mixins: [resizePageMixin],
  data() {
    return {
      curMenu: '통합',
      gender: 0,
      menus: [
        {
          title: '통합',
          value: 0
        },
        {
          title: '남성',
          value: 1
        },
        {
          title: '여성',
          value: 2
        }
      ],
      lockers: [],
      isPageLoading: false,
      lockerHistoryDialogVisible: false,
      targetLocker: undefined,
      targetHistory: undefined,
      itemSize: 100
    };
  },
  watch: {
    isLockerMenuVisible: {
      immediate: true,
      handler: function(isAccessAllowed) {
        if (!isAccessAllowed) {
          this.$router.push('/announce/access-block').catch(() => {});
        }
      }
    }
  },
  computed: {
    ...computedGetters(['isLockerMenuVisible'])
  },
  mounted() {
    this.itemSizeHandlerByScreenWidth();
  },
  methods: {
    handleClickMakeRooms() {
      this.$prompt(
        '추가하실 락커 개수를 입력해주세요.',
        `${this.curMenu} 락커 추가`,
        {
          confirmButtonText: '확인',
          cancelButtonText: '취소',
          inputPattern: /\d+/,
          inputErrorMessage: '락커 개수를 올바르게 입력해주세요.',
          customClass: 'fifl-message-box',
          confirmButtonClass: 'gray',
          cancelButtonClass: 'white',
          showClose: false
        }
      ).then(async ({ value }) => {
        try {
          await fiflboxApi.v1.postLockerRooms({
            gender: this.gender,
            is_paused: false,
            quantity: value
          });
          await this.syncLockerData();
        } catch (err) {}
      });
    },
    async handleClickLocker({ action, locker, targetHistory }) {
      if (targetHistory && action !== 'list') {
        try {
          await this.$confirm(
            '현재 이용 중인 락커입니다. 해당 회원의 상세 페이지로 이동하여 락커 내역을 먼저 수정해주세요.',
            '이용 중지 불가',
            {
              confirmButtonText: '회원 페이지로 이동',
              cancelButtonText: '확인'
            }
          );

          return this.$router
            .push(`/member/${targetHistory.enrollment_id}/`)
            .catch(() => {});
        } catch (err) {
          return;
        }
      }

      this.latestAction = action;
      switch (action) {
        case 'list':
          this.targetLocker = locker;
          this.targetHistory = targetHistory;
          this.lockerHistoryDialogVisible = true;
          return;
        case 'stop':
        case 'restart':
          try {
            const is_paused = !locker.is_paused;
            await fiflboxApi.v1.patchLockerRooms(locker.id, { is_paused });
            if (is_paused) {
              this.$message('본 락커가 이용 중지됐습니다.');
            } else {
              this.$message('이용 가능한 락커로 변경됐습니다.');
            }
            await this.syncLockerData();
          } catch (err) {}
          return;
      }
    },
    async onChangeTab(newTabMenu) {
      this.curMenu = newTabMenu.title;
      this.gender = newTabMenu.value;

      this.isPageLoading = true;
      await this.syncLockerData();
      this.isPageLoading = false;
    },
    async syncLockerData() {
      try {
        const { data: lockers } = await fiflboxApi.v1.getLockerRooms({
          gender: this.gender
        });
        this.lockers = lockers;
      } catch (err) {}
    },
    closeDialog() {
      this.lockerHistoryDialogVisible = false;
    },
    duration({ start, end }) {
      return `${dateFns.format(start, 'YYYY.MM.DD')} - ${dateFns.format(
        end,
        'YYYY.MM.DD'
      )}`;
    },
    isOccupied({ id }) {
      return (
        this.targetHistory &&
        this.targetHistory.id === id &&
        this.targetLocker.lockerStatus === LOCKER_STATUS.OCCUPIED
      );
    },
    onResize() {
      this.itemSizeHandlerByScreenWidth();
    },
    itemSizeHandlerByScreenWidth() {
      const screenStatus = window.innerWidth <= 768 ? 'mobile' : 'desktop';
      this.itemSizeCalculator(screenStatus);
    },
    itemSizeCalculator(screenStatus = 'desktop') {
      const areaWidth = this.$refs['locker-item-container'].clientWidth;
      const areaOuterPadding = screenStatus === 'desktop' ? 20 : 15;
      const itemContainerWidth = areaWidth - areaOuterPadding * 2;

      const itemsPadding = screenStatus === 'desktop' ? 9 : 5;
      const minItemWidth = screenStatus === 'desktop' ? 100 : 80;
      let itemRowCnt = 1;
      let itemSize = minItemWidth;

      while (true) {
        const tempSize =
          (itemContainerWidth - itemsPadding * (itemRowCnt - 1)) / itemRowCnt;
        if (tempSize < minItemWidth) {
          break;
        } else {
          itemRowCnt += 1;
          itemSize = tempSize;
        }
      }
      this.itemSize = itemSize;
    }
  }
};
</script>

<style lang="scss">
.locker-history-dialog__item-wrapper {
  width: 100%;
  display: flex;

  &.occupied {
    color: #4a4a4a;
  }

  .locker-history-dialog__item__name {
    width: 95px;
    margin-right: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.locker-container {
  position: relative;
  border-radius: 10px;
  background-color: #ffffff;
  min-height: 515px;
  .locker-item-container {
    padding: 11px 11px 20px 20px;
    display: flex;
    flex-wrap: wrap;
  }

  .loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .empty-message {
    margin: auto;
  }

  .warning-message-container {
    margin-bottom: 30px;
  }

  @media screen and (max-width: 768px) {
    min-height: 400px;
    .locker-item-container {
      padding: 10px 1px 15px 15px;
    }
  }
}
</style>
